'use client';

import Filters from '@/app/[festival-slug]/[locale]/(rest)/films/components/Filters';
import ThemedIcon from '@/components/ui/ThemedIcon';
import { PathEnum } from '@/i18n/locale-config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import MobileScheduleSorting from './MobileScheduleSorting';
import ScheduleSorting, { ScheduleSortOption } from './ScheduleSorting';

interface ScheduleFiltrationProps {
    /** Quick filters resolved to this festival's focus ids. */
    quickFilters: { id: string; icon: string; name: { en: string; fr: string } }[];
    resultCount: number;
    searchParams: {
        locations?: string;
        categories?: string;
        sort?: string;
    };
    filters: {
        locations: { id: string; label: string }[];
        categories: { id: string; label: string }[];
    };
    isFiltersApplied: boolean;
}

const ScheduleFiltration = (props: ScheduleFiltrationProps) => {
    const {
        resultCount,
        isFiltersApplied,
        searchParams,
        filters: { locations, categories },
    } = props;
    const { quickFilters } = props;
    const t = useTranslations();
    const router = useRouter();
    const pathname = usePathname();

    const getLabelsByIds = (
        ids: string[] | undefined,
        filters: { id: string; label: string }[],
    ) => {
        if (!ids) return [];
        return filters.filter((filter) => ids.includes(filter.id)).map((filter) => filter.label);
    };

    const resolveBasePath = () => {
        const pathKey = pathname as keyof typeof PathEnum;
        return PathEnum[pathKey] ?? pathname;
    };

    const pushWithSearchParams = (params: URLSearchParams) => {
        const basePath = resolveBasePath();
        const query = params.toString();
        router.push(query ? `${basePath}?${query}` : basePath);
    };

    const selectedLocations = searchParams.locations
        ? searchParams.locations.split(',')
        : undefined;
    const selectedCategories = searchParams.categories
        ? searchParams.categories.split(',')
        : undefined;

    const selectedLocationLabels = getLabelsByIds(selectedLocations, locations);
    const selectedCategoryLabels = getLabelsByIds(selectedCategories, categories);

    const clearAllFilters = () => {
        const params = new URLSearchParams();
        if (searchParams.sort) {
            params.set('sort', searchParams.sort);
        }
        pushWithSearchParams(params);
    };

    const clearFilter = (filterKey: string) => {
        const params = new URLSearchParams(searchParams as Record<string, string>);
        params.delete(filterKey);
        if (searchParams.sort) {
            params.set('sort', searchParams.sort);
        }
        pushWithSearchParams(params);
    };

    return (
        <>
            {/* Filters */}
            <div className="flex justify-between max-lg:flex-col-reverse flex-wrap gap-4 max-lg:pb-3">
                <Filters
                    quickFilters={quickFilters}
                    data={[
                        {
                            label: t('Filters.location'),
                            filters: locations,
                            key: 'locations',
                            selectedFilters: selectedLocations,
                        },
                        {
                            label: t('Filters.filmType'),
                            filters: categories,
                            key: 'categories',
                            selectedFilters: selectedCategories,
                        },
                    ]}
                    searchParams={searchParams}
                    truncate
                />
            </div>

            {/* Mobile Sorting */}
            <div className="lg:hidden flex items-center gap-4">
                <span className="font-helvetica-neue font-light text-gray-200 text-md">
                    {t('Sorting.title')}
                </span>
                <MobileScheduleSorting
                    currentSort={(searchParams.sort as ScheduleSortOption) || 'date'}
                />
            </div>

            {/* RESULTS AND SORTING */}
            <div className="flex justify-between gap-10">
                <div className="flex gap-3 flex-wrap items-center mt-8 max-lg:hidden">
                    <span className="font-helvetica-neue font-light text-gray-200 text-md">
                        {t('SchedulePage.results', {
                            count: resultCount,
                        })}
                    </span>
                    {isFiltersApplied && (
                        <button
                            onClick={clearAllFilters}
                            className="py-0.5 px-3 text-primary flex items-center border border-primary uppercase text-[25px] leading-8"
                        >
                            {t('Common.clearAll')}
                        </button>
                    )}

                    {/* SELECTED FILTERS */}
                    {isFiltersApplied && (
                        <>
                            {selectedLocationLabels.map((label) => (
                                <button
                                    key={label}
                                    onClick={() => clearFilter('locations')}
                                    className="h-9 py-0.5 px-3 flex items-center gap-2 border border-disabled uppercase text-[25px] leading-8 max-w-full min-w-0"
                                >
                                    <span className="truncate">{label}</span>
                                    <ThemedIcon
                                        aria-label="Remove filter"
                                        className="w-6 h-6 flex-shrink-0 text-primary"
                                        src="/img/icons/close-pink.svg"
                                        width={36}
                                        height={36}
                                    />
                                </button>
                            ))}
                            {selectedCategoryLabels.map((label) => (
                                <button
                                    key={label}
                                    onClick={() => clearFilter('categories')}
                                    className="h-9 py-0.5 px-3 flex items-center gap-2 border border-disabled uppercase text-[25px] leading-8 max-w-full min-w-0"
                                >
                                    <span className="truncate">{label}</span>
                                    <ThemedIcon
                                        aria-label="Remove filter"
                                        className="w-6 h-6 flex-shrink-0 text-primary"
                                        src="/img/icons/close-pink.svg"
                                        width={36}
                                        height={36}
                                    />
                                </button>
                            ))}
                        </>
                    )}
                </div>
                <div className="mt-8 w-fit min-w-[320px] hidden lg:flex justify-end">
                    <ScheduleSorting
                        currentSort={(searchParams.sort as ScheduleSortOption) || 'date'}
                    />
                </div>
            </div>
        </>
    );
};

export default ScheduleFiltration;
